import React, { useMemo } from "react";
import { HyperframesPlayer } from "../../hyperframesPlayer";
import { FORMAT_DIMENSIONS, FPS, buildReelComposition } from "../../lib/hyperframesCompositions";
import type { SceneMeta } from "../../api";
import type { QueueItem } from "@engine/lib/queue";
import type { FormatId } from "@engine/hyperframes/format";

type Props = {
  scene: SceneMeta;
  anchor: { top: number; left: number; width: number; height: number };
  format?: "9:16" | "1:1" | "16:9";
  /** Globales Library-Theme — färbt die Vorschau Dark/Light. */
  theme?: "dark" | "light";
};

const PREVIEW_W = 220;

export const ScenePreviewPopup: React.FC<Props> = ({ scene, anchor, format = "9:16", theme }) => {
  const dims = FORMAT_DIMENSIONS[(format as FormatId)] ?? FORMAT_DIMENSIONS["9:16"];
  const previewH = Math.round((PREVIEW_W * dims.height) / dims.width);
  const frameBg = theme === "light" ? "#f4f6fa" : "#06070b";

  const composition = useMemo(() => {
    const item = {
      id: `preview_${scene.id}`,
      kind: "reel",
      template: "HookReveal",
      category: "Reel",
      title: "Preview",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      props: { de: {}, en: {}, es: {}, fr: {} },
      languages: ["de"],
      formats: [format],
      status: "pending",
      outputs: [],
      ...(theme ? { theme } : {}),
      scenes: [
        {
          uid: `preview_${scene.id}`,
          sceneId: scene.id,
          durationSec: Math.max(2, Math.min(scene.defaultDurationSec ?? 4, 6)),
          inputs: scene.defaultInputs ?? {},
        },
      ],
    } as unknown as QueueItem;
    return buildReelComposition(item, format as FormatId, "de");
  }, [scene, format, theme]);

  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1600;
  const wantRight = anchor.left + anchor.width + PREVIEW_W + 24 < viewportW;
  const left = wantRight ? anchor.left + anchor.width + 12 : Math.max(12, anchor.left - PREVIEW_W - 12);
  const top = Math.max(12, Math.min(anchor.top, (typeof window !== "undefined" ? window.innerHeight : 900) - previewH - 80));

  return (
    <div
      className="scene-preview-popup"
      style={{
        position: "fixed",
        top,
        left,
        width: PREVIEW_W,
        zIndex: 9999,
      }}
    >
      <div
        className="scene-preview-frame"
        style={{ width: PREVIEW_W, height: previewH, background: frameBg }}
      >
        <HyperframesPlayer
          key={`${scene.id}|${theme ?? "dark"}`}
          srcDoc={composition.html}
          compositionWidth={dims.width}
          compositionHeight={dims.height}
          fps={FPS}
          loop
          autoPlay
          controls={false}
          displayWidth={PREVIEW_W}
          displayHeight={previewH}
          style={{ borderRadius: 10, background: frameBg }}
        />
      </div>
      <div className="scene-preview-meta">
        <strong>{scene.label}</strong>
        <span className="scene-preview-desc">{scene.description}</span>
      </div>
    </div>
  );
};
